/**
 * Value shape for the `agent-control-starter-chatbot-config` JSON flag: the product-tweakable copy and
 * model for the generated "Starter Chatbot" sample AI Config. Keeping it in a flag lets product edit the
 * description, prompts, models, and generation parameters without a deploy.
 *
 * Deliberately NOT in the flag (applied in code): the per-member config name/key, the internal variation
 * keys (derived from `name`), the `ld-sample-config` tag, and the agent mode.
 */
export type StarterChatbotConfigVariation = {
  /** System prompt / agent instructions. */
  instructions: string;
  /** Model config key, e.g. `Anthropic.claude-haiku-4-5-20251001`. */
  modelKey: string;
  /** Optional display name (Try It Live panels + source of the variation key). Omit for a generated name. */
  name?: string;
  /** Optional provider-native generation parameters (snake_cased, e.g. `max_tokens`). Omit for model defaults. */
  parameters?: Record<string, number | string | boolean | null>;
};

export type StarterChatbotConfigValue = {
  description: string;
  variations: StarterChatbotConfigVariation[];
  /**
   * First user turn for Try It Live, sent automatically (a live run per variation) when the drawer opens
   * so it always starts with a real side-by-side comparison. Omit to open with an empty chat instead.
   */
  seedUserMessage?: string;
  /**
   * Second user turn, prefilled into the Try It Live input so a live response to compare against the
   * auto-sent first exchange is one click away. Ignored when there's no `seedUserMessage`.
   */
  followUpMessage?: string;
};

/** Flag default — reproduces the previously hardcoded sample config, so behavior is unchanged when unset. */
export const DEFAULT_STARTER_CHATBOT_CONFIG: StarterChatbotConfigValue = {
  description: 'Sample AI Config created to get you started — compare its two variations in Try it live.',
  seedUserMessage: 'I was charged twice for my subscription this month. Can you help?',
  followUpMessage: 'Okay, I’d like a refund for the duplicate charge.',
  variations: [
    {
      name: 'Support Bot A',
      instructions:
        'You are a friendly, concise customer support agent for a SaaS company. Always be empathetic, offer a solution, and end with a follow-up question.',
      modelKey: 'Anthropic.claude-haiku-4-5-20251001',
      parameters: { temperature: 0.7, max_tokens: 200, top_p: 0.9 },
    },
    {
      name: 'Support Bot B',
      instructions:
        'You are a formal, highly professional support specialist. Be thorough, structured, and always reference company policy. Use numbered steps for solutions.',
      modelKey: 'Anthropic.claude-haiku-4-5-20251001',
      parameters: { temperature: 0.2, max_tokens: 400, top_p: 0.6 },
    },
  ],
};
